import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const RevisionDetailCard = ({ entity, entityType, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const entityId = entityType === 'order' ? entity.order_id : entity.booking_id;
  const pending = entity.pending_revision;
  const changes = pending?.changes || [];

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderChange = (change, index) => {
    if (change.type === 'modified') {
      return (
        <div key={index} className="bg-white border-2 border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-sm text-gray-800">{formatFieldName(change.field)}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <p className="text-xs font-semibold text-red-700 mb-1">Old Value</p>
              <p className="text-sm text-gray-800">{formatValue(change.old)}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-2">
              <p className="text-xs font-semibold text-green-700 mb-1">New Value</p>
              <p className="text-sm text-gray-800">{formatValue(change.new)}</p>
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'item_modified') {
      return (
        <div key={index} className="bg-white border-2 border-purple-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-purple-600" />
            <span className="font-semibold text-sm text-gray-800">Item Changed: {change.item_id}</span>
          </div>
          <div className="space-y-2 ml-6">
            {change.changes.map((itemChange, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-2">
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs font-semibold text-red-700 mb-1">{formatFieldName(itemChange.field)} (Old)</p>
                  <p className="text-xs text-gray-800">{formatValue(itemChange.old)}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-xs font-semibold text-green-700 mb-1">{formatFieldName(itemChange.field)} (New)</p>
                  <p className="text-xs text-gray-800">{formatValue(itemChange.new)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (change.type === 'items_count_changed') {
      return (
        <div key={index} className="bg-white border-2 border-orange-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span className="font-semibold text-sm text-gray-800">Items Count Changed</span>
          </div>
          <p className="text-sm text-gray-700 mt-2">
            {change.old_count} items → {change.new_count} items
          </p>
        </div>
      );
    }

    if (change.type === 'payment_added') {
      return (
        <div key={index} className="bg-white border-2 border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-sm text-gray-800">Payment Added</span>
          </div>
          <div className="bg-green-50 rounded p-2">
            <p className="text-xs text-gray-700">
              <strong>Mode:</strong> {change.new?.mode} | <strong>Amount:</strong> ₹{change.new?.amount}
            </p>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    onReject(entityType, entityId, rejectReason);
    setShowRejectModal(false);
    setRejectReason('');
  };

  return (
    <div className="bg-white border-2 border-yellow-300 rounded-xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{entityId}</h3>
            <p className="text-xs text-gray-600 mt-1">
              Requested by: <strong>{pending?.requested_by || entity.revision_requested_by}</strong>
            </p>
            <p className="text-xs text-gray-600">
              Requested at: {new Date(pending?.requested_at || entity.revision_requested_at).toLocaleString('en-IN')}
            </p>
          </div>
          <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">
            PENDING REVIEW
          </span>
        </div>
        
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between bg-white rounded-lg px-3 py-2 hover:bg-yellow-50 transition-colors mt-3"
        >
          <span className="text-sm font-semibold text-gray-700">
            {expanded ? 'Hide Changes' : 'View Changes'} ({changes.length})
          </span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Changes List */}
      {expanded && (
        <div className="p-4 space-y-3 bg-gray-50">
          {changes.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-4">No changes detected</p>
          ) : (
            changes.map((change, idx) => renderChange(change, idx))
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-4 bg-white border-t-2 border-gray-200 flex gap-3">
        <button
          onClick={() => onApprove(entityType, entityId)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-colors"
        >
          <CheckCircle className="w-5 h-5" />
          Approve Changes
        </button>
        <button
          onClick={() => setShowRejectModal(true)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-colors"
        >
          <XCircle className="w-5 h-5" />
          Reject
        </button>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Reject Changes</h2>
            <p className="text-gray-600 mb-4">Please provide a reason for rejecting these changes:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none mb-4"
              rows="4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevisionDetailCard;
